/**
 * Handles the search and filter functionality within a specified container.
 */
class FilterSearch {
    private searchInputGroup: HTMLDivElement;
    private searchInput: HTMLInputElement;
    private filterItems: NodeListOf<HTMLElement>;

    /**
     * Initializes the FilterSearch class for a specific container, caching essential DOM elements and setting up event listeners.
     * @param {HTMLDivElement} searchContainer - The container element to apply the filter search functionality.
     */
    constructor(private searchContainer: HTMLDivElement) {
        this.searchInputGroup = searchContainer.querySelector('[data-filter-search-input]') as HTMLDivElement;
        this.searchInput = this.searchInputGroup.querySelector('input') as HTMLInputElement;
        this.filterItems = searchContainer.querySelectorAll('[data-filter-item]') as NodeListOf<HTMLElement>;

        this.initEvents();
    }

    /**
     * Sets up the event listeners for the class.
     */
    private initEvents(): void {
        this.searchInput.addEventListener('input', () => this.handleSearchInput());
    }

    /**
     * Handles the input event of the search field, filtering and displaying items based on the search term.
     */
    private handleSearchInput(): void {
        const searchTerm = this.searchInput.value.toLowerCase();

        if (searchTerm) {
            Array.from(this.filterItems).forEach(item => {
                const itemName = item.getAttribute('data-filter-item')?.toLowerCase() ?? '';
                item.setAttribute('data-display', !itemName.includes(searchTerm) ? 'none' : '');
            });

            this.showSearchItems();
        } else {
            this.showAllItems();
        }
    }

    /**
     * Shows only the search input group and the filter results for the specific container.
     */
    private showSearchItems(): void {
        this.searchContainer.dataset.filterSearch = 'active';
    }

    /**
     * Resets all items to their default visibility for the specific container.
     */
    private showAllItems(): void {
        this.searchContainer.dataset.filterSearch = '';
    }
}

// Initialize the class for each search container when the document is ready
function initFilterSearch(container: HTMLDivElement) {
    new FilterSearch(container);
}

const filterSearchObserver = new MutationObserver((mutations) => {
    mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {

            if (node instanceof HTMLElement && node.matches('[data-filter-search]'))
                initFilterSearch(node as HTMLDivElement);

            if (node instanceof HTMLElement)
                node.querySelectorAll<HTMLDivElement>('[data-filter-search]').forEach(initFilterSearch);
        });
    });
});

filterSearchObserver.observe(document.body, {childList: true, subtree: true});

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll<HTMLDivElement>('[data-filter-search]').forEach(initFilterSearch);
});